/**
 * Single seam for analytics. Feature code calls trackEvent() / trackPageView();
 * nothing leaves the browser until consent === "granted" AND a connection string
 * was supplied at build time.
 *
 * Events raised before the SDK finishes loading are queued and flushed once it is
 * ready, so the first page view of a session is not lost.
 */
import { getConsent } from "./consent";
import { getClient, isConfigured } from "./appinsights";

type Props = Record<string, string | number | boolean | undefined>;

const queue: { name: string; props?: Props }[] = [];
let flushing = false;

/** Sends anything queued once the SDK is available. No-ops when unconfigured. */
async function flush(): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;
  try {
    const client = await getClient();
    if (!client) return; // unconfigured or consent withheld — leave the queue alone
    while (queue.length > 0) {
      const e = queue.shift()!;
      client.trackEvent({ name: e.name }, e.props);
    }
  } finally {
    flushing = false;
  }
}

export function trackEvent(name: string, props?: Props): void {
  if (getConsent() !== "granted") return;
  queue.push({ name, props });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ccb:analytics", { detail: { name, props } }));
  }
  if (isConfigured()) void flush();
}

/**
 * Records a page view. Called on every React Router navigation, including the
 * first render — this is the metric that answers "how much traffic did the site
 * get", so it is deliberately separate from the custom-event path.
 */
export function trackPageView(name: string, uri: string): void {
  if (getConsent() !== "granted") return;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ccb:pageview", { detail: { name, uri } }));
  }
  if (!isConfigured()) return;
  void getClient().then((client) => client?.trackPageView({ name, uri }));
}

/** Test-only: drains queued events. */
export function _drainEvents(): { name: string; props?: Props }[] {
  return queue.splice(0, queue.length);
}
